import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { FiCalendar, FiMapPin, FiAward } from 'react-icons/fi';

export default function Education() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <section id="education" className="bg-slate-50">
      <div className="section-container">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-1 h-8 bg-gradient-to-b from-primary-600 to-accent-500 rounded-full" />
            <p className="text-primary-600 font-semibold text-sm uppercase tracking-widest">Education</p>
          </div>
          <h2 className="section-title">Academic Background</h2>
          <p className="text-slate-400 text-sm">Building the technical foundation that powers real-world impact.</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="card overflow-hidden"
        >
          {/* Top gradient banner */}
          <div className="h-2 bg-gradient-to-r from-primary-500 via-blue-500 to-accent-500" />

          <div className="p-8">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
              {/* Left */}
              <div className="flex gap-4 items-start">
                {/* Logo placeholder / initials */}
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-100 to-blue-100 border border-primary-200 flex items-center justify-center flex-shrink-0 shadow-sm">
                  <span className="font-display font-bold text-primary-600 text-lg">VTU</span>
                </div>
                <div>
                  <h3 className="font-display font-bold text-slate-900 text-xl">
                    Brindavan College of Engineering
                  </h3>
                  <p className="text-slate-600 font-medium text-sm mt-0.5">Affiliated to Visvesvaraya Technological University (VTU)</p>
                  <p className="text-primary-600 font-semibold text-sm mt-1">
                    Bachelor of Engineering — Computer Science
                  </p>
                </div>
              </div>

              {/* Right */}
              <div className="flex flex-col items-start md:items-end gap-2 flex-shrink-0">
                <div className="flex items-center gap-1.5 text-sm text-slate-500">
                  <FiCalendar size={14} />
                  <span>August 2020 — August 2024</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm text-slate-500">
                  <FiMapPin size={14} />
                  <span>Bengaluru, Karnataka</span>
                </div>
                <div className="bg-primary-600 text-white text-sm font-bold px-4 py-1.5 rounded-xl shadow-sm">
                  CGPA: 8.58 / 10.0
                </div>
              </div>
            </div>

            {/* Award Highlight */}
            <div className="mt-6 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl p-4">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                <FiAward size={20} className="text-amber-600" />
              </div>
              <div>
                <p className="font-semibold text-amber-800 text-sm">Best Outgoing Student of CSE 2024 Batch</p>
                <p className="text-amber-700 text-xs mt-0.5">
                  Awarded by <strong>Anurag Thakur</strong>, Member of Lok Sabha — Brindavan College of Engineering
                </p>
              </div>
            </div>

            {/* Key Info Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
              {[
                { label: 'Degree', value: 'B.E. CS' },
                { label: 'Duration', value: '4 Years' },
                { label: 'CGPA', value: '8.58' },
                { label: 'Batch', value: '2020–2024' },
              ].map((item) => (
                <div key={item.label} className="bg-slate-50 rounded-xl p-3 border border-slate-100 text-center">
                  <p className="text-xs text-slate-400 font-medium">{item.label}</p>
                  <p className="text-sm text-slate-800 font-bold mt-0.5">{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
